<template>
  <div class="toast-container">
    <Toast
      :position="position ?? 'bottom-right'"
      :class="customClass"
      :base-z-index="9991"
    >
      <template #message="slotProps">
        <span :class="slotProps.message.iconClass"></span>
        <div class="p-toast-message-text">
          <span class="p-toast-summary" v-html="slotProps.message.summary" />
          <div class="p-toast-detail" v-html="slotProps.message.detail" />
        </div>
      </template>
    </Toast>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { Toast } from 'primevue';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { useRequestCartCheckoutStore } from '~/stores/requestCartCheckoutStore/requestCartCheckoutStore';
import { useHTMLDictionary } from '~/composables/useHTMLDictionary';
import { isDefined } from '~/utils/guards/isDefined';
import { ToastMessageType } from '~/composables/useShowToast';
import {
  TOAST_MESSAGE_SEPARATOR,
  useToastCookie
} from '~/utils/useToastCookie';
import { type CartActionType, CartTypes } from '~/stores/cartStore/model';
import { isProductDetailPage } from '~/utils/shop/isProductDetailPage';

interface Props {
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  customClass?: string;
}

withDefaults(defineProps<Props>(), {
  position: 'top-right',
  customClass: ''
});

const isPdp = computed(() => isProductDetailPage());

const htmlDictionary = useHTMLDictionary([
  'pdp.addToCart.success.message',
  'pdp.removeFromCart.success.message',
  'pdp.addToRequestCart.success.message',
  'pdp.removeFromRequestCart.success.message',
  'pdp.addToRequestCart.success.message.redirect'
]);

const cartStore = useCartStore();
const requestCartCheckoutStore = useRequestCartCheckoutStore();
const toasts = useShowToast();
const router = useRouter();
const { $textDictionary } = useNuxtApp();

const cartActionLabelMap = computed<Partial<Record<CartActionType, string>>>(
  () => ({
    addItems:
      htmlDictionary.value['pdp.addToCart.success.message'] ??
      'pdp.addToCart.success.message',
    removeItem:
      htmlDictionary.value['pdp.removeFromCart.success.message'] ??
      'pdp.removeFromCart.success.message'
  })
);
const requestCartActionLabelMap = computed<
  Partial<Record<CartActionType, string>>
>(() => ({
  addItems:
    htmlDictionary.value['pdp.addToRequestCart.success.message'] ??
    'pdp.addToRequestCart.success.message',
  removeItem:
    htmlDictionary.value['pdp.removeFromRequestCart.success.message'] ??
    'pdp.removeFromRequestCart.success.message'
}));

const cookie = useToastCookie();
watch(
  cookie,
  () => {
    if (isDefined(cookie.value) && cookie.value.context === 'cart') {
      let msg = cookie.value.message;

      if (msg.indexOf(TOAST_MESSAGE_SEPARATOR)) {
        const [dictionaryKey, fallbackMessage] = msg.split(
          TOAST_MESSAGE_SEPARATOR
        );
        msg = $textDictionary.value[dictionaryKey] ?? fallbackMessage;
      }

      toasts.showToast(msg, {
        severity: cookie.value.success
          ? ToastMessageType.SUCCESS
          : ToastMessageType.ERROR,
        life: cookie.value.timeout ?? 2000
      });
      cookie.value = null;
    }
  },
  { immediate: true }
);

// Track the last action we've shown a toast for to prevent duplicates
const lastShownAction = ref<typeof cartStore.lastAction>(null);

// Initialize on mount to capture current lastAction without showing it
onMounted(() => {
  lastShownAction.value =
    cartStore.lastAction ?? requestCartCheckoutStore.lastAction;
});

const handleAction = (newAction: ActionModel<CartActionType>) => {
  if (!newAction) {
    return;
  }

  // Only show toasts for actions that modify the cart, not for loadCart
  const actionsToShow: CartActionType[] = [
    'addItems',
    'removeItem',
    'updateItems',
    'placeOrder',
    'placeQuote'
  ];
  if (!actionsToShow.includes(newAction.type)) {
    return;
  }

  // Skip if we've already shown a toast for this exact action object
  if (lastShownAction.value === newAction) {
    return;
  }

  // Mark this action as shown
  lastShownAction.value = newAction;

  const error = newAction.error;
  if (error) {
    const message = Array.isArray(error) ? error.join('\n') : error;

    toasts.showToast(message, {
      severity: ToastMessageType.ERROR,
      life: 2000
    });

    return;
  }

  // Skip success toast for regular cart addItems on PDP
  // The expanded action bar will show the success state instead
  if (
    isPdp.value &&
    newAction.type === 'addItems' &&
    (cartStore.activeCartType === CartTypes.CART ||
      cartStore.activeCartType === CartTypes.CUSTOMER_CART)
  ) {
    return;
  }

  // Special handling for Request Cart addItems with redirect
  const isRequestCartWithRedirect =
    cartStore.activeCartType === CartTypes.REQUEST_CART &&
    newAction.type === 'addItems';

  const message = isRequestCartWithRedirect
    ? htmlDictionary.value['pdp.addToRequestCart.success.message.redirect'] ??
      'pdp.addToRequestCart.success.message.redirect'
    : cartStore.activeCartType === CartTypes.REQUEST_CART
    ? requestCartActionLabelMap.value[newAction.type]
    : cartActionLabelMap.value[newAction.type];

  if (message) {
    const pTagStrippedMessage = message.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '');

    if (isRequestCartWithRedirect) {
      toasts.showToast(pTagStrippedMessage, {
        severity: ToastMessageType.SUCCESS,
        life: 1500,
        onClose: () => {
          if (cartStore.currentCartLink) {
            enhancedNavigateTo({ path: cartStore.currentCartLink });
          }
        }
      });
    } else {
      toasts.showToast(pTagStrippedMessage, {
        severity: ToastMessageType.SUCCESS,
        life: 2000
      });
    }
  }
};

watch(
  () => cartStore.lastAction,
  (action) => isDefined(action) && handleAction(action)
);
watch(
  () => requestCartCheckoutStore.lastAction,
  (action) => isDefined(action) && handleAction(action)
);
</script>

<style scoped>
.toast-container {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
